import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { useDepartment } from '../../contexts/DepartmentContext';
import {
  UserCog, Plus, Search, Power, RotateCcw, KeyRound, Save, Pencil, Eye, Archive,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import PayRateManager from '../../components/workforce/PayRateManager';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Upload, FileText as DocIcon, Download, Trash2 } from 'lucide-react';
import { startPortalPreview } from '../../lib/portalPreview';
import { apiErrorMessage } from '../../lib/apiError';
import { useAuth } from '../../contexts/AuthContext';
import EmployeeAccessPanel from '../../components/EmployeeAccessPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = ['super_admin', 'service_manager', 'construction_manager', 'office_staff', 'technician', 'admin', 'manager', 'employee'];
const DEPARTMENTS = ['service', 'new_construction', 'office'];

const StatusPill = ({ active }) => (
  <span data-testid={`emp-status-${active ? 'active' : 'inactive'}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const RoleBadge = ({ role }) => (
  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37]">
    {(role || '—').replace(/_/g, ' ')}
  </span>
);

const Employees = () => {
  const { hasPermission } = useAuth();
  // iter160 — Phase E: gate sensitive employee actions client-side. Server
  // already enforces (strips fields + 403s on writes); these flags just hide UI.
  // View-side hiding for pay_rate / pto_balance is driven by the `*_restricted`
  // server flags, so we only track the edit/admin action perms here.
  const canEditPay  = hasPermission('edit_pay_rate');
  const canEditPto  = hasPermission('edit_pto_balance');
  const canEditRole = hasPermission('edit_employee_role_department') || hasPermission('manage_employees');
  const canResetPw  = hasPermission('reset_employee_password') || hasPermission('manage_employees');
  const canDeact    = hasPermission('deactivate_employee') || hasPermission('manage_employees');
  const canArchive  = hasPermission('archive_employee') || hasPermission('manage_employees');
  const canInvite   = hasPermission('invite_employees') || hasPermission('onboard_employees') || hasPermission('manage_employees');
  // iter161 — Basic profile fields (name, email, phone, certifications, emergency
  // contact, performance notes) are gated by `edit_employee_profile`. Legacy
  // `manage_employees` is still treated as an umbrella that includes profile edit.
  const canEditProfile = hasPermission('edit_employee_profile') || hasPermission('manage_employees');
  // Save button only useful if the user can edit *some* field on the drawer.
  const canSaveAny = canEditProfile || canEditRole || canEditPay || canEditPto;
  const [employees, setEmployees] = useState([]);
  const [tradeMissing, setTradeMissing] = useState(new Set());   // iter315
  const [divisionOpts, setDivisionOpts] = useState(DEPARTMENTS.map((k) => ({ key: k, name: k.replace(/_/g, ' ') })));
  useEffect(() => { axios.get(`${API}/divisions?include_inactive=true`, { withCredentials: true }).then((r) => setDivisionOpts(r.data)).catch(() => {}); }, []);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  // iter318 — Trade filter = Primary Trade (access scope stays separate). Starts from global context.
  const { activeDepartment } = useDepartment();
  const [filterTrade, setFilterTrade] = useState(activeDepartment && activeDepartment !== 'all' ? activeDepartment : '');
  const [primaryTrade, setPrimaryTrade] = useState({});
  const [trades, setTrades] = useState([]);
  const [searchParams] = useSearchParams();
  const [includeInactive, setIncludeInactive] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activity, setActivity] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('license');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '' });
  const [benefits, setBenefits] = useState(null);
  const [benSummary, setBenSummary] = useState(null);
  const [savingBen, setSavingBen] = useState(false);
  const canManageBenefits = canEditPto || hasPermission('manage_employees') || hasPermission('view_pto_balance');

  const refresh = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterDept) params.set('department', filterDept);
      params.set('include_inactive', (includeInactive || includeArchived) ? 'true' : 'false');
      params.set('include_archived', includeArchived ? 'true' : 'false');
      const { data } = await axios.get(`${API}/admin/employees?${params}`, { withCredentials: true });
      setEmployees(data);
      axios.get(`${API}/admin/employees-access`, { withCredentials: true }).then((r) => {
        setTradeMissing(new Set(r.data.employees.filter((x) => x.trade_not_assigned).map((x) => x.id)));
        setPrimaryTrade(Object.fromEntries(r.data.employees.map((x) => [x.id, x.primary_department_id || ''])));
      }).catch(() => {});
      axios.get(`${API}/inv/departments`, { withCredentials: true }).then((r) => setTrades((r.data || []).filter((t) => t.active !== false))).catch(() => {});
    } catch (err) { console.error(err); }
  }, [filterDept, includeInactive, includeArchived]);

  useEffect(() => { refresh(); }, [refresh]);
  // iter318 — deep link ?open=<employeeId> (Dashboard tech links) opens the canonical drawer.
  useEffect(() => {
    const id = searchParams.get('open');
    if (id && id !== selectedId) openEmployee(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openEmployee = async (id) => {
    setSelectedId(id);
    try {
      const [e, a, d] = await Promise.all([
        axios.get(`${API}/admin/employees/${id}`, { withCredentials: true }),
        axios.get(`${API}/admin/employees/${id}/activity?limit=50`, { withCredentials: true }),
        axios.get(`${API}/admin/employees/${id}/documents`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSelected({
        ...e.data,
        certifications: Array.isArray(e.data.certifications) ? e.data.certifications.join(', ') : (e.data.certifications || ''),
        emergency_contact: e.data.emergency_contact || { name: '', phone: '', relation: '' },
      });
      setActivity(a.data);
      setDocuments(d.data || []);
      setBenefits(null); setBenSummary(null);
      axios.get(`${API}/admin/employees/${id}/benefits`, { withCredentials: true })
        .then(({ data }) => { setBenefits(data.benefits); setBenSummary(data.pto_summary); })
        .catch(() => {});
    } catch (err) { toast.error('Failed to load employee'); }
  };

  const closeDrawer = () => { setSelectedId(null); setSelected(null); setActivity([]); setDocuments([]); };

  const reloadDocs = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/employees/${selectedId}/documents`, { withCredentials: true });
      setDocuments(data || []);
    } catch { /* ignore */ }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      await axios.post(`${API}/admin/employees/${selectedId}/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Uploaded');
      reloadDocs();
    } catch (err) { toast.error(apiErrorMessage(err, 'Upload failed')); }
    setUploading(false);
    e.target.value = '';
  };

  const deleteDoc = async (fid) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/admin/documents/${fid}`, { withCredentials: true });
      toast.success('Deleted');
      reloadDocs();
    } catch { toast.error('Failed'); }
  };

  const saveProfile = async () => {
    if (!selected) return;
    // iter164 — Role-change confirmation. If the admin is changing role from
    // the employee profile drawer, surface the same "apply defaults vs keep
    // custom" choice as the Permissions page so we never silently leave a
    // stale role_permissions override behind.
    const original = employees.find((e) => e.id === selectedId);
    const roleChanged = original && original.role !== selected.role;
    if (roleChanged) {
      const ok = window.confirm(
        `Change ${selected.name}'s role from "${(original.role || '').replace(/_/g, ' ')}" ` +
        `to "${(selected.role || '').replace(/_/g, ' ')}"?\n\n` +
        `Role defaults for the new role will be applied — any previously-saved ` +
        `custom permissions for this user will be cleared.\n\n` +
        `To keep custom permissions, change the role from the Roles & Permissions page instead.`,
      );
      if (!ok) {
        // Revert the in-memory role so the select reflects reality.
        setSelected({ ...selected, role: original.role });
        return;
      }
    }
    const payload = {
      name: selected.name,
      email: (selected.email || '').trim().toLowerCase(),
      phone: selected.phone || '',
      role: selected.role,
      department: selected.department,
      hire_date: selected.hire_date || '',
      pto_balance: selected.pto_balance ? Number(selected.pto_balance) : 0,
      certifications: (selected.certifications || '').split(',').map(s => s.trim()).filter(Boolean),
      emergency_contact: selected.emergency_contact || {},
      performance_notes: selected.performance_notes || '',
      manager_id: selected.manager_id || '',
      assigned_truck_id: selected.assigned_truck_id || '',
      time_tracking_enabled: selected.time_tracking_enabled !== false,
    };
    try {
      const { data } = await axios.put(`${API}/admin/employees/${selectedId}`, payload, { withCredentials: true });
      if (data?.cleared_override) {
        toast.success(`Saved — role defaults applied (custom overrides cleared)`);
      } else {
        toast.success('Saved');
      }
      refresh();
    } catch (err) {
      // iter161 — Never render the raw error object (Phase 19C preview-readonly
      // returns {detail: {code, message}} which crashes React if toast'd raw).
      // The drawer intentionally stays open so the user can correct & retry.
      toast.error(apiErrorMessage(err, 'Save failed'));
    }
  };

  const setBen = (patch) => setBenefits((b) => ({ ...(b || {}), ...patch }));

  const saveBenefits = async () => {
    if (!selected || !benefits) return;
    setSavingBen(true);
    try {
      const payload = {
        hire_date: benefits.hire_date || null,
        eligibility_override_date: benefits.eligibility_override_date || null,
        pre_agreed_pto: !!benefits.pre_agreed_pto,
        pre_agreed_hours: Number(benefits.pre_agreed_hours || 0),
        pre_agreed_effective_date: benefits.pre_agreed_effective_date || null,
        pre_agreed_available_immediately: !!benefits.pre_agreed_available_immediately,
        pre_agreed_note: benefits.pre_agreed_note || '',
        annual_allowance: Number(benefits.annual_allowance || 0),
        renewal_type: benefits.renewal_type || 'jan1',
        renewal_custom_date: benefits.renewal_custom_date || null,
        auto_renew: !!benefits.auto_renew,
        carryover_type: benefits.carryover_type || 'none',
        carryover_max_hours: Number(benefits.carryover_max_hours || 0),
        sick_pay: benefits.sick_pay || 'paid',
        paid_holiday_eligible: benefits.paid_holiday_eligible !== false,
      };
      const { data } = await axios.put(`${API}/admin/employees/${selectedId}/benefits`, payload, { withCredentials: true });
      setBenefits(data.benefits); setBenSummary(data.pto_summary);
      toast.success(data.pre_agreed_grant ? `Benefits saved · pre-agreed grant of ${data.pre_agreed_grant.hours}h posted` : 'Benefits saved');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to save benefits'));
    }
    setSavingBen(false);
  };

  const runRenewalForEmployee = async () => {
    if (!selected) return;
    try {
      const { data } = await axios.post(`${API}/admin/pto/run-renewals?employee_id=${selectedId}`, {}, { withCredentials: true });
      const r = (data.results && data.results[0]) || {};
      if (r.action === 'renewed') {
        toast.success(`Renewed ${r.period}: carried ${r.carried}h, forfeited ${r.forfeited}h, granted ${r.annual_grant}h → ${r.final_balance}h`);
      } else {
        toast.message(`No renewal: ${r.reason || 'not due'}`);
      }
      const { data: b } = await axios.get(`${API}/admin/employees/${selectedId}/benefits`, { withCredentials: true });
      setBenefits(b.benefits); setBenSummary(b.pto_summary);
    } catch (err) { toast.error(apiErrorMessage(err, 'Renewal failed')); }
  };


  const archiveEmployee = async () => {
    if (!window.confirm("Archive this employee?\n\nThey'll be removed from active dropdowns and the schedule, but all history (jobs, invoices, timesheets, approvals) stays intact. Use Reactivate to restore.")) return;
    try {
      await axios.post(`${API}/admin/employees/${selectedId}/archive`, {}, { withCredentials: true });
      toast.success('Archived');
      await openEmployee(selectedId);
      refresh();
    } catch (err) { toast.error(apiErrorMessage(err, 'Archive failed')); }
  };

  const toggleActive = async () => {
    const action = selected?.active === false ? 'reactivate' : 'deactivate';
    try {
      await axios.post(`${API}/admin/employees/${selectedId}/${action}`, {}, { withCredentials: true });
      toast.success(action === 'deactivate' ? 'Deactivated' : 'Reactivated');
      await openEmployee(selectedId);
      refresh();
    } catch (err) { toast.error(apiErrorMessage(err, 'Failed')); }
  };

  // iter303 — Restore / Rehire an ARCHIVED employee. Reuses the reactivate endpoint which
  // restores account eligibility (active:true, archived:false) and audit-logs who did it —
  // it does NOT silently recreate scheduling/payroll state; those follow normal rules.
  const restoreEmployee = async () => {
    if (!window.confirm(`Restore / rehire ${selected?.name}?\n\nThis re-enables their login and account access. It does NOT auto-restore schedule assignments, pay, or payroll state — set those via normal employee management. All history is preserved.`)) return;
    try {
      await axios.post(`${API}/admin/employees/${selectedId}/reactivate`, {}, { withCredentials: true });
      toast.success('Employee restored — login access re-enabled');
      await openEmployee(selectedId);
      refresh();
    } catch (err) { toast.error(apiErrorMessage(err, 'Restore failed')); }
  };

  const resetPassword = async () => {
    if (!window.confirm('Generate a new temporary password for this employee?')) return;
    try {
      const { data } = await axios.post(`${API}/admin/employees/${selectedId}/reset-password`, {}, { withCredentials: true });
      window.prompt('Temporary password (copy this — it will not be shown again):', data.temp_password);
      toast.success('Password reset');
    } catch (err) { toast.error(apiErrorMessage(err, 'Failed')); }
  };

  const createEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/employees`, createForm, { withCredentials: true });
      toast.success('Employee created');
      setCreateForm({ name: '', email: '', password: '' });
      setCreateOpen(false);
      refresh();
    } catch (err) { toast.error(apiErrorMessage(err, 'Failed')); }
  };

  const byTrade = !filterTrade ? employees
    : employees.filter((e) => (filterTrade === 'none' ? !primaryTrade[e.id] : primaryTrade[e.id] === filterTrade));
  const filtered = search
    ? byTrade.filter(e => `${e.name} ${e.email} ${e.role} ${e.department}`.toLowerCase().includes(search.toLowerCase()))
    : byTrade;

  return (
    <div data-testid="admin-employees-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-2"><UserCog className="w-6 h-6 text-[#D4AF37]" /> Employees</h2>
          <p className="text-[#A3A3A3]">Manage roles, departments, pay, certifications, and access</p>
        </div>
        <Sheet open={createOpen} onOpenChange={setCreateOpen}>
          {canInvite && (
            <SheetTrigger asChild>
              <Button data-testid="add-employee-btn" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> New Employee</Button>
            </SheetTrigger>
          )}
          <SheetContent side="right" className="bg-[#0D0D0D] border-[#262626] text-white">
            <SheetHeader><SheetTitle className="text-[#D4AF37]">Create Employee</SheetTitle></SheetHeader>
            <form onSubmit={createEmployee} className="mt-5 space-y-3">
              <div><Label className="text-white">Full Name</Label><Input data-testid="new-emp-name" value={createForm.name} onChange={(e) => setCreateForm({...createForm, name: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-white">Email</Label><Input data-testid="new-emp-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({...createForm, email: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-white">Temp Password</Label><Input data-testid="new-emp-password" value={createForm.password} onChange={(e) => setCreateForm({...createForm, password: e.target.value})} required minLength={6} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <Button data-testid="new-emp-submit" type="submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] w-full">Create</Button>
              <p className="text-[#525252] text-xs">Set role, department, pay rate, etc. by clicking the employee after creation.</p>
            </form>
          </SheetContent>
        </Sheet>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="employees-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, role, division" className="pl-10 bg-[#0A0A0A] border-[#262626] text-white" />
        </div>
        <select data-testid="filter-trade" value={filterTrade} onChange={(e) => setFilterTrade(e.target.value)} className="bg-[#0A0A0A] border border-[#262626] text-white text-sm rounded px-3 py-2">
          <option value="">All Trades (primary)</option>
          {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          <option value="none">Trade Not Assigned</option>
        </select>
        <select data-testid="filter-dept" value={filterDept} onChange={(e) => setFilterDept(e.target.value)} className="bg-[#0A0A0A] border border-[#262626] text-white text-sm rounded px-3 py-2">
          <option value="">All divisions</option>
          {divisionOpts.map(d => <option key={d.key} value={d.key}>{d.name}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
          <input data-testid="include-inactive" type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} className="accent-[#D4AF37]" />
          Show inactive
        </label>
        <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
          <input data-testid="include-archived" type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="accent-[#D4AF37]" />
          Show archived
        </label>
      </div>{includeArchived && (
        <p data-testid="archived-view-hint" className="text-xs text-[#737373] -mt-1">Archived employees are read-only. Open one to view their full history or Restore / Rehire.</p>
      )}

      {/* Employee list */}
      <div className="grid gap-2">
        {filtered.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No employees</div>
        ) : filtered.map((e) => (
          <button
            key={e.id}
            data-testid={`emp-row-${e.id}`}
            onClick={() => openEmployee(e.id)}
            className={`bg-[#141414] border border-[#262626] hover:border-[#D4AF37]/40 rounded-md p-4 flex items-center justify-between text-left transition-colors ${e.active === false ? 'opacity-60' : ''}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                <span className="text-[#D4AF37] font-bold">{(e.name || '?').slice(0, 1)}</span>
              </div>
              <div className="min-w-0">
                <p className="text-white font-medium truncate">{e.name}</p>
                <p className="text-[#A3A3A3] text-xs truncate">{e.email}</p>
                {tradeMissing.has(e.id) && <span data-testid={`emp-trade-missing-${e.id}`} className="inline-block mt-0.5 text-[9px] font-bold uppercase tracking-wider text-orange-300 border border-orange-500/50 rounded px-1">Trade not assigned</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {e.role !== 'super_admin' && !e.archived && (
                <button
                  data-testid={`emp-view-side-${e.id}`}
                  onClick={(ev) => { ev.stopPropagation(); startPortalPreview({ id: e.id, name: e.name, email: e.email, role: e.role }); }}
                  className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-cyan-700/40 text-cyan-300 hover:bg-cyan-900/20 text-[11px] uppercase tracking-wider"
                  title="View Their Side (read-only preview)"
                >
                  <Eye className="w-3 h-3" /> View
                </button>
              )}
              <RoleBadge role={e.role} />
              <span className="hidden sm:inline text-[10px] uppercase text-[#A3A3A3] tracking-wider">{(e.department || '—').replace(/_/g, ' ')}</span>
              {e.archived ? (
                <span data-testid={`emp-archived-badge-${e.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#525252]/20 text-[#A3A3A3] inline-flex items-center gap-1"><Archive className="w-3 h-3" /> Archived</span>
              ) : (
                <StatusPill active={e.active !== false} />
              )}
              <Pencil className="w-4 h-4 text-[#525252]" />
            </div>
          </button>
        ))}
      </div>

      {/* Drawer */}
      <Sheet open={!!selectedId} onOpenChange={(o) => { if (!o) closeDrawer(); }}>
        <SheetContent side="right" className="bg-[#0D0D0D] border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[#D4AF37] flex items-center gap-3">
                  {selected.name}
                  {selected.archived
                    ? <span data-testid="drawer-archived-badge" className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#525252]/20 text-[#A3A3A3] inline-flex items-center gap-1"><Archive className="w-3 h-3" /> Archived</span>
                    : <StatusPill active={selected.active !== false} />}
                </SheetTitle>
              </SheetHeader>
              <p className="text-[#A3A3A3] text-sm mt-1">{selected.email}</p>
              {selected.archived && (
                <div data-testid="drawer-viewonly-banner" className="mt-3 rounded-md border border-[#525252]/40 bg-[#525252]/10 p-3 text-sm text-[#D4D4D4] flex items-start gap-2">
                  <Archive className="w-4 h-4 mt-0.5 text-[#A3A3A3] shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Archived — View Only</p>
                    <p className="text-xs text-[#A3A3A3]">Login and all employee/portal API access are revoked. History below is preserved and read-only. Use <span className="text-[#D4AF37]">Restore / Rehire</span> to re-enable access.</p>
                    {selected.archived_at && <p className="text-[10px] text-[#737373] mt-1">Archived {new Date(selected.archived_at).toLocaleDateString()}</p>}
                  </div>
                </div>
              )}

              <Tabs defaultValue="profile" className="mt-5">
                <TabsList className="bg-[#0A0A0A] border border-[#262626]">
                  <TabsTrigger data-testid="drawer-tab-profile" value="profile" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">Profile</TabsTrigger>
                  <TabsTrigger data-testid="drawer-tab-documents" value="documents" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">Documents</TabsTrigger>
                  <TabsTrigger data-testid="drawer-tab-activity" value="activity" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">Activity</TabsTrigger>
                </TabsList>
                <TabsContent value="profile" className="mt-4 space-y-5">
              {/* iter303 — archived employees are strictly view-only; a disabled fieldset
                  neutralises every nested input/select/button. Restore lives outside it. */}
              <fieldset disabled={selected.archived === true} className="min-w-0 space-y-5 border-0 p-0 m-0 disabled:opacity-70">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-white">Full Name</Label><Input data-testid="profile-name" value={selected.name || ''} disabled={!canEditProfile} onChange={(e) => setSelected({...selected, name: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" /></div>
                <div><Label className="text-white">Email <span className="text-[10px] text-[#A3A3A3]">(updating clears email verification)</span></Label><Input data-testid="profile-email" type="email" value={selected.email || ''} disabled={!canEditProfile} onChange={(e) => setSelected({...selected, email: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" /></div>
                <div><Label className="text-white">Phone</Label><Input data-testid="profile-phone" value={selected.phone || ''} disabled={!canEditProfile} onChange={(e) => setSelected({...selected, phone: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" /></div>
                {/* iter315 — Role record + Primary Trade + Primary Division (+ extra scope). Saved via /access. */}
                <EmployeeAccessPanel employeeId={selected.id} canEdit={canEditRole} onSaved={(a) => { setSelected((p) => ({ ...p, role: a.role, department: a.division })); setTradeMissing((prev) => { const n = new Set(prev); if (a.trade_not_assigned) n.add(a.id); else n.delete(a.id); return n; }); }} />
                <div className="sm:col-span-2">
                  <Label className="text-white">Pay Rate ($/hr)</Label>
                  <div className="mt-1">
                    <PayRateManager employeeId={selected.id} canEdit={canEditPay} canView={!selected.pay_rate_restricted} />
                  </div>
                </div>
                <div><Label className="text-white">Hire Date</Label><Input data-testid="profile-hire-date" type="date" value={selected.hire_date || ''} disabled={!canEditProfile} onChange={(e) => setSelected({...selected, hire_date: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" /></div>
                <div>
                  <Label className="text-white">PTO Balance (hrs)</Label>
                  {selected.pto_balance_restricted ? (
                    <div data-testid="profile-pto-restricted" className="bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 text-[#A3A3A3] text-sm flex items-center gap-2">
                      <span aria-label="restricted">🔒</span> Restricted
                    </div>
                  ) : (
                    <Input data-testid="profile-pto" type="number" step="0.5" value={selected.pto_balance ?? ''} disabled={!canEditPto} onChange={(e) => setSelected({...selected, pto_balance: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" />
                  )}
                </div>
                <div>
                  <Label className="text-white">Manager</Label>
                  <Select value={selected.manager_id || '__none__'} onValueChange={(v) => setSelected({...selected, manager_id: v === '__none__' ? '' : v})} disabled={!canEditProfile}>
                    <SelectTrigger data-testid="profile-manager" className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60"><SelectValue placeholder="No manager" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626] max-h-72">
                      <SelectItem value="__none__" className="text-white">— No manager —</SelectItem>
                      {employees.filter(e => ['super_admin','admin','service_manager','construction_manager','manager'].includes(e.role) && e.id !== selectedId).map(m => (
                        <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label className="text-white">Certifications (comma-separated)</Label><Input data-testid="profile-certs" value={selected.certifications || ''} disabled={!canEditProfile} onChange={(e) => setSelected({...selected, certifications: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" placeholder="EPA, Backflow, OSHA-10" /></div>
                <div className="sm:col-span-2 grid grid-cols-3 gap-2">
                  <div><Label className="text-white text-xs">Emergency Contact Name</Label><Input data-testid="profile-ec-name" value={selected.emergency_contact?.name || ''} disabled={!canEditProfile} onChange={(e) => setSelected({...selected, emergency_contact: {...selected.emergency_contact, name: e.target.value}})} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" /></div>
                  <div><Label className="text-white text-xs">Phone</Label><Input data-testid="profile-ec-phone" value={selected.emergency_contact?.phone || ''} disabled={!canEditProfile} onChange={(e) => setSelected({...selected, emergency_contact: {...selected.emergency_contact, phone: e.target.value}})} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" /></div>
                  <div><Label className="text-white text-xs">Relation</Label><Input data-testid="profile-ec-rel" value={selected.emergency_contact?.relation || ''} disabled={!canEditProfile} onChange={(e) => setSelected({...selected, emergency_contact: {...selected.emergency_contact, relation: e.target.value}})} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" /></div>
                </div>
                <div className="sm:col-span-2"><Label className="text-white">Performance Notes</Label><Textarea data-testid="profile-perf" value={selected.performance_notes || ''} disabled={!canEditProfile} onChange={(e) => setSelected({...selected, performance_notes: e.target.value})} rows={3} className="bg-[#0A0A0A] border-[#262626] text-white disabled:opacity-60" /></div>
                <div className="sm:col-span-2 flex items-center justify-between bg-[#0A0A0A] border border-[#262626] rounded px-3 py-3">
                  <div>
                    <Label className="text-white">Time Tracking</Label>
                    <p className="text-[#A3A3A3] text-[11px] mt-0.5">When on, this employee must clock in to view their Field Schedule and receives the 8 AM / 5 PM clock reminders. Turn off for staff who don&apos;t track time.</p>
                  </div>
                  <Switch
                    data-testid="profile-time-tracking"
                    checked={selected.time_tracking_enabled !== false}
                    disabled={!canEditProfile}
                    onCheckedChange={(v) => setSelected({ ...selected, time_tracking_enabled: v })}
                  />
                </div>
              </div>

              {canManageBenefits && benefits && (
                <div data-testid="benefits-panel" className="mt-6 rounded-lg border border-[#D4AF37]/30 bg-[#0F0F0F] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold text-sm uppercase tracking-wider">Benefits &amp; PTO Setup</h3>
                    {benSummary && (
                      <span className="text-xs text-[#A3A3A3]" data-testid="benefits-current-balance">Current PTO: <span className="text-[#D4AF37] font-semibold">{Number(benSummary.current_available).toFixed(2)}h</span></span>
                    )}
                  </div>

                  <div className="mb-3 text-xs rounded-md bg-[#141414] border border-[#262626] px-3 py-2" data-testid="benefits-eligibility">
                    {benefits.is_eligible_now
                      ? <span className="text-green-400">✓ PTO eligible{benefits.eligibility_date ? ` (since ${benefits.eligibility_date})` : ''}</span>
                      : <span className="text-yellow-400">⏳ Not PTO-eligible until <b>{benefits.eligibility_date || '—'}</b> (hire date + 90 days). PTO approval is blocked until then.</span>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-white text-xs">Hire Date</Label><Input data-testid="ben-hire-date" type="date" value={benefits.hire_date || ''} onChange={(e) => setBen({ hire_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                    <div><Label className="text-white text-xs">Eligibility Override Date</Label><Input data-testid="ben-elig-override" type="date" value={benefits.eligibility_override_date || ''} onChange={(e) => setBen({ eligibility_override_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>

                    <div><Label className="text-white text-xs">Annual PTO Allowance (hrs)</Label><Input data-testid="ben-annual-allowance" type="number" step="0.5" value={benefits.annual_allowance ?? 0} onChange={(e) => setBen({ annual_allowance: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                    <div>
                      <Label className="text-white text-xs">Sick Pay Policy</Label>
                      <Select value={benefits.sick_pay || 'paid'} onValueChange={(v) => setBen({ sick_pay: v })}>
                        <SelectTrigger data-testid="ben-sick-pay" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626]">
                          <SelectItem value="paid" className="text-white">Paid (unlimited)</SelectItem>
                          <SelectItem value="unpaid" className="text-white">Unpaid (unlimited, tracked)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-white text-xs">Renewal</Label>
                      <Select value={benefits.renewal_type || 'jan1'} onValueChange={(v) => setBen({ renewal_type: v })}>
                        <SelectTrigger data-testid="ben-renewal-type" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626]">
                          <SelectItem value="jan1" className="text-white">January 1</SelectItem>
                          <SelectItem value="anniversary" className="text-white">Employee Anniversary</SelectItem>
                          <SelectItem value="custom" className="text-white">Custom Date</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {benefits.renewal_type === 'custom' && (
                      <div><Label className="text-white text-xs">Custom Renewal (MM-DD)</Label><Input data-testid="ben-renewal-custom" placeholder="07-01" value={benefits.renewal_custom_date || ''} onChange={(e) => setBen({ renewal_custom_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                    )}

                    <div>
                      <Label className="text-white text-xs">Carryover</Label>
                      <Select value={benefits.carryover_type || 'none'} onValueChange={(v) => setBen({ carryover_type: v })}>
                        <SelectTrigger data-testid="ben-carryover-type" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626]">
                          <SelectItem value="none" className="text-white">None (use it or lose it)</SelectItem>
                          <SelectItem value="unlimited" className="text-white">Unlimited</SelectItem>
                          <SelectItem value="max" className="text-white">Maximum X hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {benefits.carryover_type === 'max' && (
                      <div><Label className="text-white text-xs">Max Carryover (hrs)</Label><Input data-testid="ben-carryover-max" type="number" step="0.5" value={benefits.carryover_max_hours ?? 0} onChange={(e) => setBen({ carryover_max_hours: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-2 text-white cursor-pointer" data-testid="ben-auto-renew"><input type="checkbox" checked={!!benefits.auto_renew} onChange={(e) => setBen({ auto_renew: e.target.checked })} /> Auto-renew each year</label>
                    <label className="flex items-center gap-2 text-white cursor-pointer" data-testid="ben-paid-holiday"><input type="checkbox" checked={benefits.paid_holiday_eligible !== false} onChange={(e) => setBen({ paid_holiday_eligible: e.target.checked })} /> Paid holiday eligible</label>
                    <label className="flex items-center gap-2 text-white cursor-pointer" data-testid="ben-preagreed"><input type="checkbox" checked={!!benefits.pre_agreed_pto} onChange={(e) => setBen({ pre_agreed_pto: e.target.checked })} /> Pre-agreed PTO deal</label>
                  </div>

                  {benefits.pre_agreed_pto && (
                    <div className="mt-3 rounded-md bg-[#141414] border border-[#262626] p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><Label className="text-white text-xs">Pre-agreed Hours</Label><Input data-testid="ben-preagreed-hours" type="number" step="0.5" value={benefits.pre_agreed_hours ?? 0} onChange={(e) => setBen({ pre_agreed_hours: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                      <div><Label className="text-white text-xs">Effective Date</Label><Input data-testid="ben-preagreed-eff" type="date" value={benefits.pre_agreed_effective_date || ''} onChange={(e) => setBen({ pre_agreed_effective_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                      <label className="flex items-center gap-2 text-white text-sm cursor-pointer sm:col-span-2" data-testid="ben-preagreed-immediate"><input type="checkbox" checked={!!benefits.pre_agreed_available_immediately} onChange={(e) => setBen({ pre_agreed_available_immediately: e.target.checked })} /> Available immediately (bypass 90-day wait)</label>
                      <div className="sm:col-span-2"><Label className="text-white text-xs">Admin Note</Label><Input data-testid="ben-preagreed-note" value={benefits.pre_agreed_note || ''} onChange={(e) => setBen({ pre_agreed_note: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="e.g. Negotiated 40h at hire" /></div>
                      {benefits.pre_agreed_granted && (
                        <p className="sm:col-span-2 text-xs text-green-400">
                          ✓ Pre-agreed grant is on the ledger ({(benefits.pre_agreed_grant_hours ?? benefits.pre_agreed_hours ?? 0)}h posted).
                          Changing Pre-agreed Hours posts only the <span className="font-semibold">difference</span> as an auditable adjustment — it never duplicates the grant.
                        </p>
                      )}
                      <p className="sm:col-span-2 text-[11px] text-[#A3A3A3] leading-relaxed">
                        <span className="text-white font-semibold">How these relate:</span> the <span className="text-white">Pre-agreed deal</span> is a one-time special balance posted to the PTO ledger (the single source of truth for Current Available PTO shown here, in Time Off approval, and in My Benefits).
                        The <span className="text-white">Annual PTO Allowance</span> is the separate yearly entitlement granted at renewal. Available balance = ledger total (pre-agreed grant + adjustments + renewals + carryover − PTO used).
                      </p>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button data-testid="benefits-save" onClick={saveBenefits} disabled={savingBen} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Save className="w-4 h-4 mr-1" /> {savingBen ? 'Saving…' : 'Save Benefits'}</Button>
                    <Button data-testid="benefits-run-renewal" onClick={runRenewalForEmployee} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><RotateCcw className="w-4 h-4 mr-1" /> Run Renewal Now</Button>
                  </div>
                </div>
              )}

              </fieldset>
              <div className="mt-5 flex flex-wrap gap-2">
                {selected.archived ? (
                  canDeact && (
                    <Button
                      data-testid="profile-restore"
                      onClick={restoreEmployee}
                      className="bg-green-600 text-white hover:bg-green-500"
                      title="Re-enable login and account access (history preserved)"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Restore / Rehire
                    </Button>
                  )
                ) : (
                  <>
                {canSaveAny && (
                  <Button data-testid="profile-save" onClick={saveProfile} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Save className="w-4 h-4 mr-1" /> Save</Button>
                )}
                {canResetPw && (
                  <Button data-testid="profile-reset-pw" onClick={resetPassword} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><KeyRound className="w-4 h-4 mr-1" /> Reset Password</Button>
                )}
                {canDeact && (
                  <Button
                    data-testid="profile-toggle-active"
                    onClick={toggleActive}
                    variant="outline"
                    className={`${selected.active === false ? 'border-green-600 text-green-400 hover:bg-green-900/30' : 'border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white'}`}
                  >
                    {selected.active === false ? <><RotateCcw className="w-4 h-4 mr-1" /> Reactivate</> : <><Power className="w-4 h-4 mr-1" /> Deactivate</>}
                  </Button>
                )}
                {canArchive && !selected.archived && (
                  <Button
                    data-testid="profile-archive"
                    onClick={archiveEmployee}
                    variant="outline"
                    className="border-[#525252] text-[#A3A3A3] hover:bg-[#262626] hover:text-white"
                    title="Remove from active dropdowns but keep all history"
                  >
                    <Archive className="w-4 h-4 mr-1" /> Archive
                  </Button>
                )}
                  </>
                )}
              </div>
                </TabsContent>

                <TabsContent value="documents" className="mt-4 space-y-4">
                  {selected.archived && (
                    <p data-testid="docs-viewonly-note" className="text-xs text-[#737373]">Archived — documents are read-only (download only).</p>
                  )}
                  <fieldset disabled={selected.archived === true} className="border-0 p-0 m-0 disabled:opacity-70">
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <Label className="text-white text-xs">Type</Label>
                      <select data-testid="doc-type" value={docType} onChange={(e) => setDocType(e.target.value)} className="bg-[#0A0A0A] border border-[#262626] text-white rounded h-10 px-3 text-sm">
                        <option value="license">License</option>
                        <option value="certification">Certification</option>
                        <option value="id">ID</option>
                        <option value="contract">Contract</option>
                        <option value="general">General</option>
                      </select>
                    </div>
                    <label data-testid="doc-upload-label" className="cursor-pointer inline-flex items-center gap-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] rounded px-3 py-2 text-sm font-medium">
                      <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload File'}
                      <input data-testid="doc-upload-input" type="file" className="hidden" onChange={uploadDoc} disabled={uploading} />
                    </label>
                  </div>
                  <div className="bg-[#141414] border border-[#262626] rounded">
                    {documents.length === 0 ? (
                      <p className="text-[#525252] text-center text-sm py-6">No documents yet</p>
                    ) : documents.map((d) => (
                      <div key={d.file_id} data-testid={`doc-row-${d.file_id}`} className="flex items-center justify-between gap-3 px-4 py-2 border-b border-[#262626] last:border-0">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <DocIcon className="w-4 h-4 text-[#D4AF37] shrink-0" />
                          <div className="min-w-0">
                            <p className="text-white text-sm truncate">{d.filename}</p>
                            <p className="text-[#525252] text-xs">{d.doc_type} · {Math.round((d.size || 0) / 1024)} KB · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ''}</p>
                          </div>
                        </div>
                        <a data-testid={`doc-download-${d.file_id}`} href={`${API}/admin/documents/${d.file_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#F3E5AB] text-xs"><Download className="w-3 h-3" /> View</a>
                        <button data-testid={`doc-delete-${d.file_id}`} onClick={() => deleteDoc(d.file_id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                  </fieldset>
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <div className="space-y-1 max-h-80 overflow-y-auto bg-[#141414] border border-[#262626] rounded-md p-3">
                    {activity.length === 0 ? (
                      <p className="text-[#525252] text-xs text-center py-4">No recent activity</p>
                    ) : activity.map((a) => (
                      <div key={a.id} className="text-xs flex items-center justify-between border-b border-[#262626] last:border-0 py-1">
                        <span className="text-white"><span className="text-[#D4AF37] font-mono">{a.action}</span> <span className="text-[#A3A3A3]">by {a.actor_name}</span></span>
                        <span className="text-[#525252]">{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Employees;
